import { startCase } from "lodash";

import { RouterTextLink } from "components/actions/RouterLink";
import Popover from "components/ui/popover/Popover";
import { appRoutes } from "lib/appRoutes";
import { relationshipLink } from "./notice_relationship/relationshipLink";

import css from "./OrganisationsPopover.module.scss";

// Allows including id or guid but not requiring both while still requiring at least one
type OrgOld = { id: number | string; name: string; guid?: string };
type OrgNew = { guid: string; name: string; id?: number | string };

type OrgProps = {
  orgs: (OrgOld | OrgNew)[];
  orgType: "buyers" | "suppliers";
  authorised?: boolean;
  onUnauthorisedClick?: () => boolean;
  /** Links each supplier to its relationship with this buyer instead of to its profile. */
  relationshipBuyerGuid?: string | null;
};

// One of id/guid is always present (see OrgOld/OrgNew); prefer the guid since it's the
// canonical URL form (numeric ids still resolve via redirect). Fall back to "" to keep
// the route param typed as string|number for the appRoutes builders.
function orgRoute(
  orgType: "buyers" | "suppliers",
  org: OrgOld | OrgNew,
  relationshipBuyerGuid?: string | null,
) {
  if (orgType === "suppliers" && relationshipBuyerGuid && org.guid) {
    return relationshipLink(relationshipBuyerGuid, org.guid);
  }
  const id = org.guid ?? org.id ?? "";
  return orgType === "buyers" ? appRoutes.buyerDetail(id) : appRoutes.supplierDetail(id);
}

export default function OrganisationsContainer(props: OrgProps) {
  const { orgs, orgType, authorised, onUnauthorisedClick, relationshipBuyerGuid } = props;

  if (orgs.length === 0) {
    return <span>--</span>;
  }

  if (orgs.length === 1) {
    return (
      <RouterTextLink
        {...orgRoute(orgType, orgs[0], relationshipBuyerGuid)}
        authorised={authorised}
        onUnauthorisedClick={onUnauthorisedClick}
        className={css.link}
      >
        {orgs[0].name}
      </RouterTextLink>
    );
  }

  return <OrgPopover {...props} />;
}

function OrgPopover({
  orgs,
  orgType,
  authorised,
  onUnauthorisedClick,
  relationshipBuyerGuid,
}: OrgProps) {
  const total = orgs.length;
  const orgTypeLabel = startCase(orgType);

  return (
    <Popover
      trigger={<span className={css.orgProperty}>{`${total} ${orgTypeLabel}`}</span>}
      hideArrow
    >
      <div className={css.popoverTitle}>{`${total} ${orgTypeLabel}`}</div>
      <div className={css.popoverContent}>
        {orgs.map((org) => (
          <RouterTextLink
            {...orgRoute(orgType, org, relationshipBuyerGuid)}
            authorised={authorised}
            onUnauthorisedClick={onUnauthorisedClick}
            className={css.link}
            key={org.id ?? org.guid}
          >
            {org.name}
          </RouterTextLink>
        ))}
      </div>
    </Popover>
  );
}
